import { styled, ThemeProvider } from "styled-components";

import { CrossIcon, UIButton } from "common/components";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { darkTheme, lightTheme } from "domains/theme/constants/theme";
import { useTheme } from "domains/theme/useTheme";
import { useGoToMembership } from "entities/user/utils";

const StyledContainer = styled.div`
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.colors.backgroundPrimary};
  color: ${({ theme }) => theme.colors.textBright};
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 50px;
  max-height: 600px;
  transition:
    min-height 0.3s,
    max-height 0.3s;
  border-radius: 5px;
  margin-top: 3rem;
`;

const StyledCloseIcon = styled(CrossIcon)`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 25px;
  height: 25px;
  color: ${({ theme }) => theme.colors.textFade};
  cursor: pointer;
  margin-left: 3rem;
  transition: all 0.3s;
  border-radius: 20px;
  padding: 7px;
  &:hover {
    color: ${({ theme }) => theme.colors.textBright};
    background: ${({ theme }) => theme.colors.textBright}0F;
  }
`;

const StyledLabel = styled.p`
  margin-bottom: 1rem;
  line-height: 1.5rem;
  color: ${({ theme }) => theme.colors.textFade};
`;

const StyledTitle = styled.h3`
  font-size: 1.4rem;
  margin: 0;
`;

type UpgradeAccountForCreateCallToActionProps = {
  isCallToActionVisible: boolean;
  setCallToActionVisible: (value: boolean) => void;
  type: "publicCTA" | "digitalMemberCTA";
};

export const UpgradeAccountForCreateCallToAction = ({
  isCallToActionVisible,
  setCallToActionVisible,
  type,
}: UpgradeAccountForCreateCallToActionProps) => {
  const goToMembership = useGoToMembership();
  const theme = useTheme();
  const track = useTrack();
  const translate = useTranslate();

  const onClick = () => {
    track({ name: "Upgrade" });
    goToMembership();
  };

  const onDismissClick = () => setCallToActionVisible(false);

  const contentLocalisedKeys =
    type === "publicCTA"
      ? ({
          description: "app.create.landing_banner.no_permissions_for_map_creation_description",
          title: "app.create.landing_banner.no_permissions_for_map_creation_title",
        } as const)
      : ({
          description: "app.create.landing_banner.no_permissions_for_ai_description",
          title: "app.create.landing_banner.no_permissions_for_ai_title",
        } as const);

  return isCallToActionVisible ? (
    <ThemeProvider theme={theme === "light" ? darkTheme : lightTheme}>
      <StyledContainer>
        <StyledCloseIcon onClick={onDismissClick} />
        <StyledTitle>{translate(contentLocalisedKeys.title)}</StyledTitle>
        <StyledLabel>{translate(contentLocalisedKeys.description)}</StyledLabel>
        <UIButton variant="filled" onClick={onClick}>
          {translate("app.registration.app.create.landing_banner.no_permissions_upgrade_button")}
        </UIButton>
      </StyledContainer>
    </ThemeProvider>
  ) : null;
};
